import { RefObject, useRef } from 'react';
import { applyStatics, MenuItem } from '@szhsin/react-menu';
import styled from 'styled-components';
import {
  useMaybeTrade,
  useUnifiedTradesFetchData,
  useUnifiedTradesIdsList,
  useUnifiedTradesService,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trades';
import { Trade } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { isRejectedTrade } from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import ActionsBarEmpty from '#/components/UnifiedRfqs/components/bottom-tabs/ActionsBarEmpty';
import {
  TrackingCtxProvider,
  useTrackedCallback,
} from '@paradigm/features/src/analytics';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';
import ContextMenuProvider, {
  ContextMenu as BaseContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '../context-menu/context-menu';
import BaseTable from '../components/BaseTable';

import TradeRow, { TradeHeaderRow } from './TradeRow';
import TradeDetailsModal from './TradeDetailsModal';

function ContextMenu() {
  const { detailsItemId: tradeId } = useContextMenuView();
  const { createRfq } = useContextMenuActions();
  const datadogPrefix = 'Trade History Table Context Menu';
  const handleCreateRfq = useTrackedCallback(
    'create market',
    () => {
      createRfq('RFQ');
    },
    { auctionType: 'RFQ' },
  );
  const handleCreateOb = useTrackedCallback(
    'create market',
    () => {
      createRfq('OB');
    },
    { auctionType: 'OB' },
  );

  return (
    <BaseContextMenu>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateRfq}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateOb}
      />
      {tradeId != null && <ViewDetailsMenuItem tradeId={tradeId} />}
    </BaseContextMenu>
  );
}

// This is needed if we want to wrap menu item
applyStatics(MenuItem)(ViewDetailsMenuItem);
function ViewDetailsMenuItem({ tradeId, ...props }: { tradeId: Trade['id'] }) {
  const { openDetailsModal } = useContextMenuActions();
  const trade = useMaybeTrade(tradeId);
  const isRejectedDrfq = trade?.rfqType === 'drfq' && isRejectedTrade(trade);
  const isRejectedGrfq =
    trade?.rfqType === 'grfq' && trade.status === 'REJECTED';
  const handleViewDetails = useTrackedCallback(
    'view trade recap',
    openDetailsModal,
  );
  if (isRejectedDrfq || isRejectedGrfq) return null;

  return (
    <MenuItem {...props} onClick={handleViewDetails}>
      View Details
    </MenuItem>
  );
}

export default function TradesHistory() {
  return (
    <TrackingCtxProvider value="trade history tab">
      <ContextMenuProvider>
        <TradeDetailsModal />
        <TradesHistoryContent />
        <ContextMenu />
      </ContextMenuProvider>
    </TrackingCtxProvider>
  );
}

function TradesHistoryContent() {
  const { status, canFetchMore } = useUnifiedTradesFetchData();
  const { fetcher } = useUnifiedTradesService();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const tradesIds = useUnifiedTradesIdsList();

  return (
    <Container>
      <ActionsBarEmpty />
      <BaseTable
        status={status}
        canFetchMore={canFetchMore}
        fetcher={fetcher}
        isEmpty={tradesIds.length === 0}
        entityRowsList={
          <TradesRowsList tradesIds={tradesIds} scrollableRef={scrollableRef} />
        }
        headerRow={<TradeHeaderRow />}
        dataTestId="unified-rfq-trade-history-table"
        scrollableRef={scrollableRef}
        emptyListMessage="No trades happened today"
      />
    </Container>
  );
}

const Container = styled.div`
  flex: 1; // take 100% heigh
  display: flex;
  flex-direction: column;
`;

function TradesRowsList(props: {
  scrollableRef: RefObject<HTMLDivElement>;
  tradesIds: readonly Trade['id'][];
}) {
  return (
    <>
      {props.tradesIds.map((id) => (
        <TradeRow key={id} tradeId={id} scrollableRef={props.scrollableRef} />
      ))}
    </>
  );
}
